import { useState, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useTheme } from '@contexts/ThemeContext'
import DataManagerSidebar from '@components/data-manager/DataManagerSidebar'
import ImportWizard from '@components/data-manager/ImportWizard'
import DataBrowser from '@components/data-manager/DataBrowser'
import AuditLogViewer from '@components/data-manager/AuditLogViewer'
import { dataManagerApi } from '@services/dataManagerApi'
import './DataManagerLayout.css'

type ActiveView =
  | 'import'
  | 'audit'
  | 'sales-transactions'
  | 'products'
  | 'sales-pipeline'
  | 'customers'
  | 'accounts'
  | 'gl-transactions'
  | 'suppliers'
  | 'employees'
  | 'competitors'

interface DomainConfig {
  domain: string
  path: string
  label: string
  columns: string[]
  readOnlyColumns?: string[]
}

const DOMAIN_CONFIGS: Record<string, DomainConfig> = {
  'sales-transactions': {
    domain: 'sales',
    path: 'transactions',
    label: 'Sales Transactions',
    columns: ['id', 'order_number', 'transaction_date', 'customer_name', 'product_name', 'quantity', 'unit_price', 'cost_of_goods', 'line_total', 'status'],
    readOnlyColumns: ['customer_name'],
  },
  products: {
    domain: 'sales',
    path: 'products',
    label: 'Products',
    columns: ['id', 'sku', 'name', 'category', 'cost_price', 'list_price', 'is_active'],
  },
  'sales-pipeline': {
    domain: 'sales',
    path: 'pipeline',
    label: 'Sales Pipeline',
    columns: ['id', 'deal_name', 'stage', 'expected_value', 'probability', 'expected_close'],
  },
  customers: {
    domain: 'customers',
    path: '',
    label: 'Customers',
    columns: ['id', 'name', 'company_name', 'segment', 'status', 'first_purchase_date'],
  },
  accounts: {
    domain: 'accounting',
    path: 'accounts',
    label: 'Chart of Accounts',
    columns: ['id', 'account_code', 'account_name', 'account_type', 'parent_code', 'is_active'],
  },
  'gl-transactions': {
    domain: 'accounting',
    path: 'gl-transactions',
    label: 'GL Transactions',
    columns: ['id', 'transaction_date', 'account_code', 'description', 'amount', 'category'],
  },
  suppliers: {
    domain: 'accounting',
    path: 'suppliers',
    label: 'Suppliers',
    columns: ['id', 'name', 'category', 'city', 'status', 'payment_terms'],
  },
  employees: {
    domain: 'employees',
    path: '',
    label: 'Employees',
    columns: ['id', 'name', 'role', 'department', 'status', 'hire_date'],
  },
  competitors: {
    domain: 'competitors',
    path: '',
    label: 'Competitors',
    columns: ['id', 'name', 'city', 'category', 'rating', 'review_count'],
  },
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

// Hardcoded for now — will be dynamic once multi-company support lands
const COMPANY_ID = 1

function DataManagerLayout() {
  const [activeView, setActiveView] = useState<ActiveView>('import')
  const [recordCounts, setRecordCounts] = useState<Record<string, number> | null>(null)
  const { theme } = useTheme()

  // Fetch record counts on mount and after imports
  useEffect(() => {
    dataManagerApi.getRecordCounts(COMPANY_ID).then(setRecordCounts).catch(() => {})
  }, [activeView])

  const renderContent = () => {
    switch (activeView) {
      case 'import':
        return <ImportWizard companyId={COMPANY_ID} />
      case 'audit':
        return <AuditLogViewer companyId={COMPANY_ID} />
      default: {
        const config = DOMAIN_CONFIGS[activeView]
        if (config) {
          return <DataBrowser companyId={COMPANY_ID} config={config} />
        }
        return <div>Unknown view</div>
      }
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="dm-layout" data-theme={theme}>
        <header className="dm-header">
          <h1 className="dm-header-title">Data Manager</h1>
          <span className="dm-header-company">Better Hearing For You</span>
        </header>
        <div className="dm-body">
          <DataManagerSidebar activeView={activeView} onNavigate={setActiveView} recordCounts={recordCounts} />
          <main className="dm-content">
            {renderContent()}
          </main>
        </div>
      </div>
    </QueryClientProvider>
  )
}

export default DataManagerLayout
